import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle,
  Building2,
  Check,
  Clock3,
  Copy,
  Link2,
  Loader2,
  LockKeyhole,
  PlugZap,
  RefreshCw,
  ShieldCheck,
  Unplug,
  X,
} from 'lucide-react';

import { useTheme } from '../contexts/ThemeContext.tsx';
import { useUserPermissions } from '../hooks/useUserPermissions.ts';
import McpClientIcon from './mcpConnectionBrands.tsx';
import {
  listMcpConnections,
  revokeMcpConnection,
  type McpConnection,
} from '../services/mcpApi.ts';

export const MCP_ENDPOINT = 'https://app.sminy.ai/mcp';

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return 'Ainda não utilizada';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Data indisponível';
  }

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const getStatusLabel = (status: string): string =>
  status === 'active' ? 'Ativa' : 'Desconectada';

interface ConnectionsSectionProps {
  isDark: boolean;
  connections: McpConnection[];
  isLoading: boolean;
  canAccessMcp: boolean;
  onDisconnect: (connection: McpConnection) => void;
}

const ConnectionsSection: React.FC<ConnectionsSectionProps> = ({
  isDark,
  connections,
  isLoading,
  canAccessMcp,
  onDisconnect,
}) => {
  const surfaceClass = isDark
    ? 'border-slate-700 bg-slate-800/70'
    : 'border-[#E4E8EF] bg-white';
  const secondaryTextClass = isDark ? 'text-slate-400' : 'text-slate-500';
  const showClinicName =
    new Set(connections.flatMap(connection => connection.clinics?.map(clinic => clinic.id) ?? [connection.clinic_id])).size > 1;

  return (
    <section id="conexoes-ativas" aria-labelledby="connections-title" className="scroll-mt-6">
      <div className="mb-4">
        <h2 id="connections-title" className={`text-xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-sminy-text-primary'}`}>
          Assistentes conectados
        </h2>
        <p className={`mt-1 text-sm ${secondaryTextClass}`}>
          Veja quais assistentes podem consultar as informações da sua clínica.
        </p>
      </div>

      {!canAccessMcp ? (
        <div role="note" className={`flex items-start gap-3 rounded-2xl border p-5 text-sm ${surfaceClass}`}>
          <LockKeyhole className={`mt-0.5 h-4 w-4 flex-shrink-0 ${isDark ? 'text-slate-300' : 'text-slate-500'}`} />
          <p className={secondaryTextClass}>
            Sua equipe não possui acesso ao MCP. Solicite a liberação ao administrador da clínica.
          </p>
        </div>
      ) : isLoading ? (
        <div className={`overflow-hidden rounded-2xl border ${surfaceClass}`} aria-busy="true">
          {[0, 1].map(item => (
            <div
              key={item}
              aria-hidden="true"
              className={`h-24 animate-pulse border-b last:border-b-0 ${isDark ? 'border-slate-700 bg-slate-800' : 'border-[#E4E8EF] bg-slate-100'}`}
            />
          ))}
          <span className="sr-only" role="status">Carregando conexões...</span>
        </div>
      ) : connections.length === 0 ? (
        <div className={`rounded-2xl border p-8 text-center sm:p-10 ${surfaceClass}`}>
          <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-2xl ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-500'}`}>
            <Link2 className="h-6 w-6" />
          </div>
          <h3 className={`mt-4 text-lg font-semibold ${isDark ? 'text-white' : 'text-sminy-text-primary'}`}>Nenhum assistente conectado</h3>
          <p className={`mx-auto mt-2 max-w-md text-sm ${secondaryTextClass}`}>
            Use o passo a passo abaixo para conectar seu primeiro assistente.
          </p>
        </div>
      ) : (
        <div className={`overflow-hidden rounded-2xl border ${surfaceClass}`}>
          {connections.map(connection => {
            const isActive = connection.status === 'active';
            const includesCrmReport = connection.scopes.includes('crm:report:read');
            const includesAds = connection.scopes.includes('ads:read');
            const includesFinancial = connection.scopes.includes('metrics:financial:read');
            return (
              <article key={connection.id} className={`flex flex-col gap-4 border-b p-5 last:border-b-0 sm:flex-row sm:items-center sm:justify-between ${isDark ? 'border-slate-700' : 'border-[#E4E8EF]'}`}>
                <div className="flex min-w-0 items-center gap-3">
                  <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl ${isDark ? 'bg-sminy-primary/15 text-sminy-blue-light' : 'bg-sminy-blue-light text-sminy-primary'}`}>
                    <McpClientIcon clientName={connection.client_name} className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className={`truncate text-sm font-semibold ${isDark ? 'text-white' : 'text-sminy-text-primary'}`}>{connection.client_name}</h3>
                      <span className={`flex-shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        isActive
                          ? isDark ? 'bg-emerald-400/10 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
                          : isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {getStatusLabel(connection.status)}
                      </span>
                    </div>
                    {showClinicName && (
                      <div className={`mt-1 flex items-center gap-1.5 text-xs ${secondaryTextClass}`}>
                        <Building2 className="h-3.5 w-3.5" />
                        <span className="break-words">{connection.clinics?.map(clinic => clinic.name).join(', ') || connection.clinic_name}</span>
                      </div>
                    )}
                    <div className={`mt-1 flex items-center gap-1.5 text-xs ${secondaryTextClass}`}>
                      <Clock3 className="h-3.5 w-3.5" />
                      <span>Última atividade: {formatDateTime(connection.last_used_at)}</span>
                    </div>
                    <p className={`mt-2 text-xs leading-5 ${secondaryTextClass}`}>
                      {includesCrmReport
                        ? includesFinancial
                          ? 'Relatório CRM com dados de clientes e valores comerciais'
                          : 'Relatório CRM com dados de clientes, sem valores comerciais'
                        : 'Métricas agregadas, sem dados de clientes'}
                      {includesCrmReport && '. Inclui histórico de agendamentos, comparecimentos e compras'}
                      {includesAds && (includesFinancial
                        ? '. Anúncios do CRM e do Gerenciador da Meta, incluindo investimento e valores monetários'
                        : '. Anúncios do CRM e do Gerenciador da Meta, sem investimento ou valores monetários')}
                    </p>
                  </div>
                </div>

                {isActive && (
                  <button
                    type="button"
                    onClick={() => onDisconnect(connection)}
                    className={`inline-flex min-h-10 flex-shrink-0 items-center justify-center gap-2 self-start rounded-xl border px-4 text-sm font-medium transition-colors sm:self-center ${isDark ? 'border-red-400/30 text-red-300 hover:bg-red-400/10' : 'border-red-200 text-red-600 hover:bg-red-50'}`}
                  >
                    <Unplug className="h-4 w-4" />
                    Desconectar
                  </button>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

interface ConnectGuideProps {
  isDark: boolean;
  isCopied: boolean;
  onCopy: () => void;
}

const ConnectGuide: React.FC<ConnectGuideProps> = ({ isDark, isCopied, onCopy }) => {
  const surfaceClass = isDark
    ? 'border-slate-700 bg-slate-800/70'
    : 'border-[#E4E8EF] bg-white';
  const secondaryTextClass = isDark ? 'text-slate-400' : 'text-slate-500';
  const stepNumberClass = `flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
    isDark ? 'bg-sminy-primary/15 text-sminy-blue-light' : 'bg-sminy-blue-light text-sminy-primary'
  }`;

  return (
    <section aria-labelledby="connect-title">
      <div className={`rounded-2xl border p-5 sm:p-6 ${surfaceClass}`}>
        <h2 id="connect-title" className={`text-xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-sminy-text-primary'}`}>
          Conectar novo assistente
        </h2>
        <ol className="mt-5 space-y-5">
          <li className="flex items-start gap-3">
            <span className={stepNumberClass} aria-hidden="true">1</span>
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                Copie o endereço de conexão
              </p>
              <button
                type="button"
                onClick={onCopy}
                className="mt-2 inline-flex min-h-10 items-center gap-2 rounded-xl bg-sminy-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-sminy-primary-hover focus:outline-none focus:ring-2 focus:ring-sminy-primary focus:ring-offset-2"
              >
                {isCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {isCopied ? 'Endereço copiado' : 'Copiar endereço'}
              </button>
              <details className={`mt-3 max-w-md rounded-xl border ${isDark ? 'border-slate-700 bg-slate-800/40' : 'border-[#E4E8EF] bg-white/70'}`}>
                <summary className={`cursor-pointer list-none px-4 py-2.5 text-xs font-medium ${secondaryTextClass}`}>
                  Mostrar endereço
                </summary>
                <div className={`border-t px-4 py-2.5 ${isDark ? 'border-slate-700' : 'border-[#E4E8EF]'}`}>
                  <code className={`block break-all text-xs leading-5 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{MCP_ENDPOINT}</code>
                </div>
              </details>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className={stepNumberClass} aria-hidden="true">2</span>
            <p className={`text-sm leading-6 ${secondaryTextClass}`}>
              <span className={`font-medium ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>Cole o endereço no seu assistente</span>
              {' '}(ChatGPT, Claude ou outro), no campo de conexão de aplicativos.
            </p>
          </li>
          <li className="flex items-start gap-3">
            <span className={stepNumberClass} aria-hidden="true">3</span>
            <p className={`text-sm leading-6 ${secondaryTextClass}`}>
              <span className={`font-medium ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>Autorize o acesso</span>
              {' '}na tela de confirmação que o assistente abrir. Pronto — ele aparecerá na lista acima.
            </p>
          </li>
        </ol>
        <p className={`mt-5 text-xs leading-5 ${secondaryTextClass}`}>
          Para consultar nomes, telefones e tags, o assistente precisa solicitar acesso ao relatório CRM em uma nova autorização. Para consultar anúncios, também é necessária uma nova autorização com acesso aos anúncios.
          {' '}Conexões existentes mantêm as permissões exibidas acima.
        </p>
      </div>
    </section>
  );
};

const McpConnections: React.FC = () => {
  const { isDark } = useTheme();
  const [connections, setConnections] = useState<McpConnection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [connectionToDisconnect, setConnectionToDisconnect] = useState<McpConnection | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const copyResetTimer = useRef<number | null>(null);

  const { hasPermission } = useUserPermissions();
  const canAccessMcp = hasPermission('mcp');
  const surfaceClass = isDark
    ? 'border-slate-700 bg-slate-800/70'
    : 'border-[#E4E8EF] bg-white';
  const secondaryTextClass = isDark ? 'text-slate-400' : 'text-slate-500';

  const loadConnections = async () => {
    if (!canAccessMcp) {
      setError('Sua equipe não possui acesso ao MCP. Solicite a liberação ao administrador da clínica.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      setConnections(await listMcpConnections());
    } catch {
      setError('Não foi possível carregar as conexões. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadConnections();
    // A lista é carregada uma vez para o usuário atual.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessMcp]);

  useEffect(() => () => {
    if (copyResetTimer.current !== null) {
      window.clearTimeout(copyResetTimer.current);
    }
  }, []);

  useEffect(() => {
    if (!connectionToDisconnect) {
      return undefined;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isDisconnecting) {
        setConnectionToDisconnect(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [connectionToDisconnect, isDisconnecting]);

  const handleCopyEndpoint = async () => {
    if (!navigator.clipboard?.writeText) {
      setError('Seu navegador não permitiu copiar o endereço de conexão. Abra o endereço abaixo e copie manualmente.');
      return;
    }

    try {
      await navigator.clipboard.writeText(MCP_ENDPOINT);
      setIsCopied(true);
      if (copyResetTimer.current !== null) {
        window.clearTimeout(copyResetTimer.current);
      }
      copyResetTimer.current = window.setTimeout(() => setIsCopied(false), 2200);
    } catch {
      setError('Não foi possível copiar o endereço de conexão. Tente novamente.');
    }
  };

  const handleDisconnect = async () => {
    if (!connectionToDisconnect || isDisconnecting) {
      return;
    }

    setIsDisconnecting(true);
    setError('');

    try {
      await revokeMcpConnection(connectionToDisconnect.id);
      const disconnectedAt = new Date().toISOString();
      setConnections(currentConnections => currentConnections.map(connection => (
        connection.id === connectionToDisconnect.id
          ? { ...connection, status: 'revoked', revoked_at: disconnectedAt }
          : connection
      )));
      setConnectionToDisconnect(null);
    } catch {
      setError('Não foi possível desconectar este assistente. Tente novamente.');
      setConnectionToDisconnect(null);
    } finally {
      setIsDisconnecting(false);
    }
  };

  const disconnectDialog = connectionToDisconnect
    ? createPortal(
        <div className="fixed inset-0 z-[9999] flex items-end justify-center p-0 sm:items-center sm:p-4">
          <button
            type="button"
            aria-label="Fechar confirmação"
            className="fixed inset-0 cursor-default bg-slate-950/50 backdrop-blur-sm"
            onClick={() => {
              if (!isDisconnecting) setConnectionToDisconnect(null);
            }}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="disconnect-mcp-title"
            aria-describedby="disconnect-mcp-description"
            className={`relative z-[10000] w-full rounded-t-2xl border p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] shadow-2xl sm:max-w-md sm:rounded-2xl ${surfaceClass}`}
          >
            <button
              type="button"
              aria-label="Fechar"
              disabled={isDisconnecting}
              onClick={() => setConnectionToDisconnect(null)}
              className={`absolute right-4 top-4 rounded-lg p-2 transition-colors ${isDark ? 'text-slate-400 hover:bg-slate-700 hover:text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'}`}
            >
              <X className="h-5 w-5" />
            </button>

            <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${isDark ? 'bg-red-400/10 text-red-300' : 'bg-red-50 text-red-600'}`}>
              <Unplug className="h-5 w-5" />
            </div>
            <h2 id="disconnect-mcp-title" className={`mt-4 text-lg font-semibold ${isDark ? 'text-white' : 'text-sminy-text-primary'}`}>Desconectar assistente?</h2>
            <p id="disconnect-mcp-description" className={`mt-2 text-sm leading-relaxed ${secondaryTextClass}`}>
              {connectionToDisconnect.client_name} deixará de consultar as informações {connectionToDisconnect.clinics && connectionToDisconnect.clinics.length > 1 ? 'de todas as clínicas desta conexão' : 'da sua clínica'}. Nenhum dado será alterado.
            </p>

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                autoFocus
                disabled={isDisconnecting}
                onClick={() => setConnectionToDisconnect(null)}
                className={`min-h-11 rounded-xl border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60 ${isDark ? 'border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700' : 'border-[#D8DEE8] bg-white text-slate-700 hover:bg-slate-50'}`}
              >
                Manter conectado
              </button>
              <button
                type="button"
                disabled={isDisconnecting}
                onClick={() => void handleDisconnect()}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isDisconnecting && <Loader2 className="h-4 w-4 animate-spin" />}
                {isDisconnecting ? 'Desconectando...' : 'Desconectar'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#111827] text-gray-200' : 'bg-gradient-to-b from-gray-50 to-white text-gray-800'} p-4 sm:p-6 lg:p-8`}>
      <main className="mx-auto max-w-6xl space-y-5">
        <header className={`rounded-2xl border ${surfaceClass} p-5`}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sminy-primary/10 text-sminy-primary">
                <PlugZap className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h1 className={`text-lg font-semibold tracking-tight ${isDark ? 'text-white' : 'text-sminy-text-primary'}`}>
                  Conexões de IA
                </h1>
                <p className={`mt-1 text-sm ${secondaryTextClass}`}>
                  Conecte assistentes como ChatGPT ou Claude para consultar as informações da sua clínica.
                </p>
              </div>
            </div>
            {canAccessMcp && (
              <button
                type="button"
                onClick={() => void loadConnections()}
                disabled={isLoading}
                className={`inline-flex min-h-10 w-fit items-center justify-center gap-2 rounded-xl border px-3.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  isDark
                    ? 'border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700'
                    : 'border-[#D8DEE8] bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                Atualizar
              </button>
            )}
          </div>
        </header>

        {error && (
          <div role="alert" className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${isDark ? 'border-red-400/30 bg-red-400/10 text-red-200' : 'border-red-200 bg-red-50 text-red-700'}`}>
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <ConnectionsSection
          isDark={isDark}
          connections={connections}
          isLoading={isLoading}
          canAccessMcp={canAccessMcp}
          onDisconnect={setConnectionToDisconnect}
        />

        {canAccessMcp && (
          <ConnectGuide
            isDark={isDark}
            isCopied={isCopied}
            onCopy={() => void handleCopyEndpoint()}
          />
        )}

        <p className={`flex items-start gap-2 text-xs leading-5 ${secondaryTextClass}`}>
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          Acesso somente de leitura: os assistentes consultam as informações sem alterar seus dados, e você pode desconectar quando quiser.
        </p>
      </main>

      {disconnectDialog}
    </div>
  );
};

export default McpConnections;
